import { useState } from 'react';
import { processUpload, ProcessUploadOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, CheckCircle, Download, FileText } from 'lucide-react';

type Category = { id: string; name: string };
type ContentType = 'SOP' | 'Training Module' | 'Induction Section';
type Result = ProcessUploadOutputType;

const TAGS = ['Service & Floor', 'Cellar & Beer', 'Safety & Hygiene', 'Admin & Finance', 'Guest Management', 'Training', 'Equipment', 'Licensing'];

interface Props {
  categories: Category[];
  onSuccess: () => void;
}

export default function PasteTextTab({ categories, onSuccess }: Props) {
  const [title, setTitle] = useState('');
  const [rawContent, setRawContent] = useState('');
  const [contentType, setContentType] = useState<ContentType>('SOP');
  const [categoryId, setCategoryId] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [generateQuiz, setGenerateQuiz] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const toggleTag = (tag: string) => {
    setSelectedTags(t => t.includes(tag) ? t.filter(x => x !== tag) : [...t, tag]);
  };

  const handleProcess = async () => {
    if (!title.trim() || !rawContent.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const res = await processUpload({
        title,
        rawContent,
        contentType,
        categoryId: categoryId || undefined,
        department: department || undefined,
        tags: selectedTags,
        generateQuiz,
      });
      setResult(res);
      toast.success(`✓ Content processed! Module created${res.questionsCreated > 0 ? ` with ${res.questionsCreated} quiz questions` : ''}`);
      onSuccess();
      setTitle('');
      setRawContent('');
      setSelectedTags([]);
      setCategoryId('');
    } catch (e: any) {
      toast.error(e.message || 'Processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const downloadContent = () => {
    if (!result) return;
    const text = `${result.enhancedTitle}\n\n${result.description}\n\n---\n\nModule ID: ${result.moduleId}\nKnowledge Base Entry: ${result.kbEntryId}\nQuiz Questions Created: ${result.questionsCreated}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.enhancedTitle}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground/80 rounded-lg">
        <p className="font-medium text-foreground mb-1">📋 Paste Text</p>
        <p>Copy and paste the text from any document, SOP, or training manual. AI will enhance, structure it, add it to the knowledge bank, and generate quiz questions automatically.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Document Title *</label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Glasswasher Cleaning Procedure" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Content Type</label>
          <Select value={contentType} onValueChange={v => setContentType(v as ContentType)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="SOP">SOP (Standard Operating Procedure)</SelectItem>
              <SelectItem value="Training Module">Training Module</SelectItem>
              <SelectItem value="Induction Section">Induction Section</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Category</label>
          <Select value={categoryId || 'none'} onValueChange={v => setCategoryId(v === 'none' ? '' : v)}>
            <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No category</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Department</label>
          <Select value={department || 'none'} onValueChange={v => setDepartment(v === 'none' ? '' : v)}>
            <SelectTrigger><SelectValue placeholder="Select dept." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">All departments</SelectItem>
              <SelectItem value="All">All</SelectItem>
              <SelectItem value="Front of House">Front of House</SelectItem>
              <SelectItem value="Back of House">Back of House</SelectItem>
              <SelectItem value="Kitchen">Kitchen</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Tags</label>
        <div className="flex flex-wrap gap-1.5">
          {TAGS.map(tag => (
            <button key={tag} onClick={() => toggleTag(tag)}
              className={`text-xs px-2.5 py-1 border rounded transition-colors ${selectedTags.includes(tag) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Paste Document Content *</label>
        <Textarea
          value={rawContent}
          onChange={e => setRawContent(e.target.value)}
          placeholder="Paste the full text from your SOP, training document, or policy here. AI will clean it up, add structure, and create quiz questions automatically..."
          className="min-h-64 font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground mt-1">{rawContent.length} characters · {Math.ceil(rawContent.split(/\s+/).filter(Boolean).length / 200)} min read</p>
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" id="genQuiz" checked={generateQuiz} onChange={e => setGenerateQuiz(e.target.checked)} className="accent-primary" />
        <label htmlFor="genQuiz" className="text-sm text-foreground">Generate quiz questions (8 questions, ready to review)</label>
      </div>

      <Button onClick={handleProcess} disabled={processing || !title.trim() || !rawContent.trim()} className="w-full bg-primary text-primary-foreground" size="lg">
        {processing
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing with AI — this may take 20-30 seconds...</>
          : <><Sparkles className="w-4 h-4 mr-2" /> Process with AI & Add to Knowledge Bank</>}
      </Button>

      {result && (
        <div className="border border-primary/30 bg-primary/5 p-4 space-y-3 rounded-lg">
          <div className="flex items-center gap-2 text-primary">
            <CheckCircle className="w-4 h-4" />
            <span className="font-semibold text-sm">Successfully processed!</span>
          </div>
          <div className="text-sm space-y-1">
            <p className="text-foreground"><strong>Title:</strong> {result.enhancedTitle}</p>
            <p className="text-muted-foreground"><strong>Description:</strong> {result.description}</p>
            <div className="flex gap-3 text-xs text-muted-foreground mt-2">
              <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> Module created</span>
              <span>·</span>
              <span>Added to Knowledge Bank</span>
              {result.questionsCreated > 0 && <><span>·</span><span>{result.questionsCreated} quiz questions generated</span></>}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Module is saved as a <strong>Draft</strong> — publish it in the Modules tab when ready.</p>
          <Button size="sm" variant="outline" onClick={downloadContent}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download summary
          </Button>
        </div>
      )}
    </div>
  );
}
